/**
 * ebml-probe.js — прочитать метаданные Matroska/WebM (теги, координаты,
 * длительность, дорожки), не читая файл целиком.
 *
 * Зачем
 * -----
 * MediaInfo (наша JS-сборка, `full: true`) НЕ отдаёт тег `LOCATION` из
 * Matroska вообще: на файле, где ffprobe показывает
 * `TAG:LOCATION=+43.5855+39.7231/`, в general-дорожке из 52 полей нет ни
 * одного, похожего на координаты (проверено 2026-08-23). Для страницы, чья
 * первая строка называется «Что раскрывает этот файл», это худший вид
 * ошибки — ЛОЖНОЕ «чисто»: человек видит, что опасного ничего нет, и
 * выкладывает видео с точкой съёмки внутри.
 *
 * Модуль умеет читать И стирать. Стирание — той же формы, что у mp4
 * (`mp4-meta-scrub.js`): элемент не вырезается, а гасится в `Void` ТОЙ ЖЕ
 * общей длины, поэтому Cluster'ы не двигаются, позиции в `SeekHead`/`Cues`
 * остаются верными, а выход собирается из ссылок `file.slice()`.
 * Длина Void подбирается: его ID занимает 1 байт, а размер в EBML — VINT
 * переменной длины, и неизбыточная запись разрешена. Значит для элемента
 * любой длины >= 2 подбирается заголовок ровно нужного размера.
 *
 * Как читает
 * ----------
 * Теги в Matroska лежат в `Segment\Tags`, и находятся они либо в голове
 * файла, либо в хвосте. Полный обход не нужен: `Segment\SeekHead` содержит
 * позиции разделов. Поэтому берём голову (несколько МБ), идём по детям
 * Segment, пока они не кончились или не начались `Cluster` (дальше только
 * медиаданные), и, если `Tags` не встретился, спрашиваем позицию у SeekHead
 * и читаем ровно тот кусок. Хвост читается только когда без него никак.
 *
 * API
 * ---
 *   EbmlProbe.looksSupported(file) -> bool
 *   EbmlProbe.scrub(file, ops) -> Promise<{blob, wiped, bytesPatched}>
 *   EbmlProbe.read(file) -> Promise<{
 *       tags, gps, raw,                      — теги и координаты
 *       duration, container,                 — из Info
 *       video[], audio[], subs[], chapters[] — из Tracks/Chapters
 *   }>
 *
 * Бросает со стабильной причиной: not-matroska, no-segment, short-read,
 * bad-ebml.
 */
(function () {
'use strict';
if (window.EbmlProbe) return;

var HEAD_BYTES = 4 * 1024 * 1024;      // сколько читаем от начала
var TAIL_BYTES = 2 * 1024 * 1024;      // и с конца, если Tags не нашлись
var MAX_REGION = 16 * 1024 * 1024;     // потолок на один дочитанный кусок

function err(reason) { var e = new Error(reason); e.ebmlReason = reason; return e; }

function looksSupported(file) {
    var n = String((file && file.name) || '').toLowerCase();
    var t = String((file && file.type) || '').toLowerCase();
    return /\.(mkv|webm|mka|mks)$/.test(n) || t === 'video/x-matroska' || t === 'video/webm';
}

function slice(file, from, to) {
    return new Promise(function (resolve, reject) {
        var fr = new FileReader();
        fr.onload = function () { resolve(new Uint8Array(fr.result)); };
        fr.onerror = function () { reject(err('short-read')); };
        fr.readAsArrayBuffer(file.slice(from, to));
    });
}

/* Идентификатор EBML: длина закодирована позицией первой единицы в старшем
   байте. Возвращаем сырое значение ВМЕСТЕ с маркером — именно так их
   принято записывать в спецификации (0x1A45DFA3 и т.п.). */
function readId(buf, pos, end) {
    if (pos >= end) return null;
    var b = buf[pos], len = 0;
    if (b & 0x80) len = 1; else if (b & 0x40) len = 2; else if (b & 0x20) len = 3; else if (b & 0x10) len = 4; else return null;
    if (pos + len > end) return null;
    var v = 0;
    for (var i = 0; i < len; i++) v = v * 256 + buf[pos + i];
    return { id: v, len: len };
}

/* Размер: то же кодирование, но маркер снимается. `all ones` = неизвестная
   длина (живой случай у Segment в потоковых файлах). */
function readSize(buf, pos, end) {
    if (pos >= end) return null;
    var b = buf[pos], len = 0;
    if (b & 0x80) len = 1; else if (b & 0x40) len = 2; else if (b & 0x20) len = 3; else if (b & 0x10) len = 4;
    else if (b & 0x08) len = 5; else if (b & 0x04) len = 6; else if (b & 0x02) len = 7; else if (b & 0x01) len = 8;
    else return null;
    if (pos + len > end) return null;
    var v = b & (0xff >> len), unknown = (v === (0xff >> len));
    for (var i = 1; i < len; i++) {
        v = v * 256 + buf[pos + i];
        if (buf[pos + i] !== 0xff) unknown = false;
    }
    return { size: v, len: len, unknown: unknown };
}

function children(buf, from, to, cb) {
    var pos = from, guard = 0;
    while (pos < to && guard++ < 100000) {
        var id = readId(buf, pos, to); if (!id) return;
        var sz = readSize(buf, pos + id.len, to); if (!sz) return;
        var body = pos + id.len + sz.len;
        var end = sz.unknown ? to : Math.min(to, body + sz.size);
        if (end < body) return;
        if (cb(id.id, body, end, sz.unknown) === false) return;
        if (sz.unknown) return;                 // дальше границы не известны
        pos = body + sz.size;
    }
}

function str(buf, from, to) {
    try { return new TextDecoder('utf-8').decode(buf.subarray(from, to)).replace(/\0+$/, '').trim(); }
    catch (_) { return ''; }
}
function uint(buf, from, to) { var v = 0; for (var i = from; i < to; i++) v = v * 256 + buf[i]; return v; }

var ID = {
    Segment: 0x18538067, SeekHead: 0x114D9B74, Seek: 0x4DBB, SeekID: 0x53AB, SeekPosition: 0x53AC,
    Info: 0x1549A966, Title: 0x7BA9, DateUTC: 0x4461, MuxingApp: 0x4D80, WritingApp: 0x5741,
    TimestampScale: 0x2AD7B1, Duration: 0x4489,
    Tags: 0x1254C367, Tag: 0x7373, SimpleTag: 0x67C8, TagName: 0x45A3, TagString: 0x4487,
    Cluster: 0x1F43B675, Tracks: 0x1654AE6B, Cues: 0x1C53BB6B, Chapters: 0x1043A770,
    TrackEntry: 0xAE, TrackType: 0x83, CodecID: 0x86, TrackName: 0x536E, Language: 0x22B59C,
    LanguageBCP47: 0x22B59D, DefaultDuration: 0x23E383, FlagDefault: 0x88, FlagForced: 0x55AA,
    Video: 0xE0, PixelWidth: 0xB0, PixelHeight: 0xBA, DisplayWidth: 0x54B0, DisplayHeight: 0x54BA,
    Audio: 0xE1, SamplingFrequency: 0xB5, Channels: 0x9F, AudioBitDepth: 0x6264,
    EditionEntry: 0x45B9, ChapterAtom: 0xB6, ChapterTimeStart: 0x91, ChapterDisplay: 0x80, ChapString: 0x85
};

/* Число с плавающей точкой в EBML — 4 или 8 байт IEEE. */
function flt(buf, from, to) {
    var n = to - from;
    var dv = new DataView(buf.buffer, buf.byteOffset, buf.length);
    if (n === 4) return dv.getFloat32(from);
    if (n === 8) return dv.getFloat64(from);
    return 0;
}

/* CodecID Matroska -> человеческое имя. Список закрытый: показать
   "V_MPEG4/ISO/AVC" вместо "AVC" значит показать НЕ ТО, что показывал
   MediaInfo, а страница обязана говорить теми же словами. */
var CODEC = {
    'V_MPEG4/ISO/AVC': 'AVC', 'V_MPEGH/ISO/HEVC': 'HEVC', 'V_MPEG4/ISO/ASP': 'MPEG-4 Visual',
    'V_MPEG2': 'MPEG-2 Video', 'V_MPEG1': 'MPEG-1 Video', 'V_VP8': 'VP8', 'V_VP9': 'VP9',
    'V_AV1': 'AV1', 'V_THEORA': 'Theora', 'V_PRORES': 'ProRes', 'V_MS/VFW/FOURCC': 'VFW',
    'A_AAC': 'AAC', 'A_OPUS': 'Opus', 'A_VORBIS': 'Vorbis', 'A_AC3': 'AC-3', 'A_EAC3': 'E-AC-3',
    'A_DTS': 'DTS', 'A_FLAC': 'FLAC', 'A_TRUEHD': 'TrueHD', 'A_MPEG/L3': 'MPEG Audio',
    'A_MPEG/L2': 'MPEG Audio', 'A_PCM/INT/LIT': 'PCM', 'A_PCM/INT/BIG': 'PCM', 'A_PCM/FLOAT/IEEE': 'PCM',
    'S_TEXT/UTF8': 'UTF-8', 'S_TEXT/ASS': 'ASS', 'S_TEXT/SSA': 'SSA', 'S_TEXT/WEBVTT': 'WebVTT',
    'S_VOBSUB': 'VobSub', 'S_HDMV/PGS': 'PGS'
};
function codecName(id) {
    if (!id) return '';
    if (CODEC[id]) return CODEC[id];
    var base = id.split('/')[0];
    return CODEC[base] || id.replace(/^[VAS]_/, '');
}

/* Имя тега Matroska → ключ, которым его знает инструмент. Имена совпадают с
   ключами `-metadata` ffmpeg, чтобы читатель и писатель говорили об одном. */
var TAG_MAP = {
    TITLE: 'title', ARTIST: 'artist', PERFORMER: 'artist', ALBUM: 'album',
    COMMENT: 'comment', DESCRIPTION: 'description', SUMMARY: 'synopsis',
    COPYRIGHT: 'copyright', DIRECTOR: 'director', COMPOSER: 'composer',
    GENRE: 'genre', DATE_RECORDED: 'date', DATE: 'date', ENCODER: 'encoder',
    'ENCODED_BY': 'encoder'
};
/* Имена, под которыми в Matroska встречаются координаты. Ни одно из них
   MediaInfo не показывает. */
var GPS_NAMES = ['LOCATION', 'GPS', 'GPS_COORDINATES', 'GEO_LOCATION', 'COORDINATES'];

function parseISO6709(s) {
    var m = String(s || '').match(/([+-]\d+(?:\.\d+)?)([+-]\d+(?:\.\d+)?)/);
    if (!m) return null;
    var lat = parseFloat(m[1]), lon = parseFloat(m[2]);
    if (!isFinite(lat) || !isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
    return { lat: lat, lon: lon };
}

function collect(buf, from, to, out) {
    children(buf, from, to, function (id, b, e) {
        if (id === ID.Tags) {
            children(buf, b, e, function (tid, tb, te) {
                if (tid !== ID.Tag) return;
                children(buf, tb, te, function (sid, sb, se) {
                    if (sid !== ID.SimpleTag) return;
                    var name = '', value = '';
                    children(buf, sb, se, function (fid, fb, fe) {
                        if (fid === ID.TagName) name = str(buf, fb, fe).toUpperCase();
                        else if (fid === ID.TagString) value = str(buf, fb, fe);
                    });
                    if (name && value) out.raw[name] = value;
                });
            });
        } else if (id === ID.Tracks) {
            children(buf, b, e, function (tid, tb, te) {
                if (tid !== ID.TrackEntry) return;
                var tr = { type: 0, codec: '', lang: '', name: '', frameNs: 0, def: 0, forced: 0,
                           width: 0, height: 0, sampleRate: 0, channels: 0, bitDepth: 0 };
                children(buf, tb, te, function (fid, fb, fe) {
                    if (fid === ID.TrackType) tr.type = uint(buf, fb, fe);
                    else if (fid === ID.CodecID) tr.codec = str(buf, fb, fe);
                    else if (fid === ID.Language || fid === ID.LanguageBCP47) tr.lang = tr.lang || str(buf, fb, fe);
                    else if (fid === ID.TrackName) tr.name = str(buf, fb, fe);
                    else if (fid === ID.DefaultDuration) tr.frameNs = uint(buf, fb, fe);
                    else if (fid === ID.FlagDefault) tr.def = uint(buf, fb, fe);
                    else if (fid === ID.FlagForced) tr.forced = uint(buf, fb, fe);
                    else if (fid === ID.Video) children(buf, fb, fe, function (vid, vb, ve) {
                        if (vid === ID.PixelWidth) tr.width = uint(buf, vb, ve);
                        else if (vid === ID.PixelHeight) tr.height = uint(buf, vb, ve);
                    });
                    else if (fid === ID.Audio) children(buf, fb, fe, function (aid, ab, ae2) {
                        if (aid === ID.SamplingFrequency) tr.sampleRate = Math.round(flt(buf, ab, ae2));
                        else if (aid === ID.Channels) tr.channels = uint(buf, ab, ae2);
                        else if (aid === ID.AudioBitDepth) tr.bitDepth = uint(buf, ab, ae2);
                    });
                });
                if (!tr.type) return;
                var entry = {
                    codec: codecName(tr.codec), codecId: tr.codec, lang: tr.lang, name: tr.name,
                    def: tr.def, forced: tr.forced
                };
                if (tr.type === 1) {
                    entry.width = tr.width; entry.height = tr.height;
                    // DefaultDuration - наносекунды на кадр; частота кадров обратна ей.
                    entry.fps = tr.frameNs ? Math.round(1e9 / tr.frameNs * 1000) / 1000 : 0;
                    out.video.push(entry);
                } else if (tr.type === 2) {
                    entry.sampleRate = tr.sampleRate; entry.channels = tr.channels; entry.bitDepth = tr.bitDepth;
                    out.audio.push(entry);
                } else if (tr.type === 17) {
                    out.subs.push(entry);
                }
            });
        } else if (id === ID.Chapters) {
            children(buf, b, e, function (cid, cb, ce) {
                if (cid !== ID.EditionEntry) return;
                children(buf, cb, ce, function (aid, ab, ae2) {
                    if (aid !== ID.ChapterAtom) return;
                    var ch = { time: null, title: '' };
                    children(buf, ab, ae2, function (fid, fb, fe) {
                        if (fid === ID.ChapterTimeStart) ch.time = uint(buf, fb, fe) / 1e9;
                        else if (fid === ID.ChapterDisplay) children(buf, fb, fe, function (did, db, de) {
                            if (did === ID.ChapString) ch.title = str(buf, db, de);
                        });
                    });
                    if (ch.time != null) out.chapters.push(ch);
                });
            });
        } else if (id === ID.Info) {
            children(buf, b, e, function (iid, ib, ie) {
                if (iid === ID.Title) out.raw.TITLE = out.raw.TITLE || str(buf, ib, ie);
                else if (iid === ID.MuxingApp) out.raw.ENCODER = out.raw.ENCODER || str(buf, ib, ie);
                else if (iid === ID.WritingApp) out.raw.WRITING_APP = out.raw.WRITING_APP || str(buf, ib, ie);
                else if (iid === ID.TimestampScale) out.timescale = uint(buf, ib, ie) || 1000000;
                else if (iid === ID.Duration) out.durationRaw = flt(buf, ib, ie);
                else if (iid === ID.DateUTC) {
                    // наносекунды от 2001-01-01
                    var ns = uint(buf, ib, ie);
                    if (ns > 0) out.raw.DATE = new Date(978307200000 + ns / 1e6).toISOString();
                }
            });
        }
    });
}

/* Позиции разделов из SeekHead — они относительны началу тела Segment. */
function seekPositions(buf, from, to) {
    var pos = {};
    children(buf, from, to, function (id, b, e) {
        if (id !== ID.SeekHead) return;
        children(buf, b, e, function (sid, sb, se) {
            if (sid !== ID.Seek) return;
            var target = 0, at = -1;
            children(buf, sb, se, function (fid, fb, fe) {
                if (fid === ID.SeekID) target = uint(buf, fb, fe);
                else if (fid === ID.SeekPosition) at = uint(buf, fb, fe);
            });
            if (target && at >= 0) pos[target] = at;
        });
    });
    return pos;
}

async function read(file) {
    if (!looksSupported(file)) throw err('not-matroska');
    var head = await slice(file, 0, Math.min(HEAD_BYTES, file.size));
    var segBody = -1, segEnd = -1;
    children(head, 0, head.length, function (id, b, e) {
        if (id === ID.Segment) { segBody = b; segEnd = e; return false; }
    });
    if (segBody < 0) throw err('no-segment');

    var out = { tags: {}, gps: null, raw: {}, video: [], audio: [], subs: [], chapters: [],
                timescale: 1000000, durationRaw: 0, duration: 0, container: '' };
    collect(head, segBody, Math.min(segEnd, head.length), out);

    // Tags в голове не нашлись — спрашиваем позицию у SeekHead.
    if (!Object.keys(out.raw).length || !GPS_NAMES.some(function (n) { return out.raw[n]; })) {
        var seek = seekPositions(head, segBody, Math.min(segEnd, head.length));
        var rel = seek[ID.Tags];
        if (rel != null) {
            var abs = segBody + rel;
            if (abs >= 0 && abs < file.size) {
                var chunk = await slice(file, abs, Math.min(file.size, abs + MAX_REGION));
                collect(chunk, 0, chunk.length, out);
            }
        } else if (file.size > HEAD_BYTES) {
            // SeekHead нет (бывает у файлов «на лету») — пробуем хвост.
            var tail = await slice(file, Math.max(0, file.size - TAIL_BYTES), file.size);
            collect(tail, 0, tail.length, out);
        }
    }

    out.duration = out.durationRaw ? (out.durationRaw * out.timescale / 1e9) : 0;
    var nm = String(file.name || '').toLowerCase(), ty = String(file.type || '').toLowerCase();
    out.container = (nm.indexOf('.webm') >= 0 || ty.indexOf('webm') >= 0) ? 'WebM' : 'Matroska';
    Object.keys(out.raw).forEach(function (name) {
        var k = TAG_MAP[name];
        if (k && !out.tags[k]) out.tags[k] = out.raw[name];
        if (!out.gps && GPS_NAMES.indexOf(name) >= 0) out.gps = parseISO6709(out.raw[name]);
    });
    return out;
}

/* ══════════════════════════════════════════════════════════════════════
   СТИРАНИЕ
   ══════════════════════════════════════════════════════════════════════ */

/* Заголовок Void ровно на `total` байт: 0xEC + размер VINT, тело — нули.
   Возвращает Uint8Array длины total либо null, если так не выходит. */
function voidBytes(total) {
    if (total < 2) return null;
    for (var sl = 1; sl <= 8; sl++) {
        var hdr = 1 + sl;
        if (total < hdr) continue;
        var body = total - hdr;
        // VINT длины sl вмещает значения до 2^(7*sl) - 2 (все единицы значат
        // «длина неизвестна» и здесь запрещены).
        var cap = Math.pow(2, 7 * sl) - 2;
        if (body > cap) continue;
        var out = new Uint8Array(total);
        out[0] = 0xEC;
        var v = body;
        for (var i = sl; i >= 1; i--) { out[i] = v & 0xff; v = Math.floor(v / 256); }
        out[1] |= (0x80 >> (sl - 1));
        return out;
    }
    return null;
}

/* Что гасит каждый пункт пульта. Имена — те же, что у читателя выше:
   один список на чтение и запись, иначе они разъедутся. */
var OP_TAGNAMES = {
    gps: GPS_NAMES,
    date: ['DATE_RECORDED', 'DATE', 'DATE_RELEASED', 'DATE_ENCODED'],
    software: ['ENCODER', 'ENCODED_BY', 'WRITING_APP', 'MUXING_APP'],
    device: ['DEVICE', 'MAKE', 'MODEL', 'CAMERA'],
    tags: ['TITLE', 'ARTIST', 'PERFORMER', 'ALBUM', 'COMMENT', 'DESCRIPTION',
           'SUMMARY', 'COPYRIGHT', 'DIRECTOR', 'COMPOSER', 'GENRE']
};

/* Собрать список диапазонов (абсолютных), которые надо погасить. */
function planVoids(buf, base, from, to, ops, plan) {
    var wantAll = !!ops.all;
    var wanted = {};
    Object.keys(OP_TAGNAMES).forEach(function (op) {
        if (wantAll || ops[op]) OP_TAGNAMES[op].forEach(function (n) { wanted[n] = 1; });
    });

    children(buf, from, to, function (id, b, e, unknown) {
        if (unknown) return;
        var elEnd = e;
        // Начало элемента = b минус его заголовок; children() отдаёт тело,
        // поэтому границу элемента восстанавливаем по предыдущему концу.
        if (id === ID.Tags) {
            if (wantAll) { plan.push({ from: base + elStartOf(buf, b), to: base + elEnd }); return; }
            children(buf, b, e, function (tid, tb, te) {
                if (tid !== ID.Tag) return;
                children(buf, tb, te, function (sid, sb, se) {
                    if (sid !== ID.SimpleTag) return;
                    var name = '';
                    children(buf, sb, se, function (fid, fb, fe) {
                        if (fid === ID.TagName) name = str(buf, fb, fe).toUpperCase();
                    });
                    if (name && wanted[name]) plan.push({ from: base + elStartOf(buf, sb), to: base + se });
                });
            });
        } else if (id === ID.Info) {
            children(buf, b, e, function (iid, ib, ie) {
                var hit = (iid === ID.Title && (wantAll || ops.tags))
                    || (iid === ID.DateUTC && (wantAll || ops.date))
                    || ((iid === ID.MuxingApp || iid === ID.WritingApp) && (wantAll || ops.software));
                if (hit) plan.push({ from: base + elStartOf(buf, ib), to: base + ie });
            });
        }
    });
}

/* children() отдаёт начало ТЕЛА; чтобы погасить элемент целиком, нужен его
   собственный старт. Идём назад по заголовку: сначала размер, потом ID. */
function elStartOf(buf, bodyStart) {
    for (var back = 2; back <= 12; back++) {
        var p = bodyStart - back;
        if (p < 0) break;
        var id = readId(buf, p, buf.length);
        if (!id) continue;
        var sz = readSize(buf, p + id.len, buf.length);
        if (!sz) continue;
        if (p + id.len + sz.len === bodyStart) return p;
    }
    return bodyStart;   // не нашли — гасим хотя бы тело
}

async function scrub(file, ops) {
    ops = ops || {};
    if (!looksSupported(file)) throw err('not-matroska');

    var head = await slice(file, 0, Math.min(HEAD_BYTES, file.size));
    var segBody = -1, segEnd = -1;
    children(head, 0, head.length, function (id, b, e) {
        if (id === ID.Segment) { segBody = b; segEnd = e; return false; }
    });
    if (segBody < 0) throw err('no-segment');

    var plan = [];
    planVoids(head, 0, segBody, Math.min(segEnd, head.length), ops, plan);

    /* Tags часто лежат в хвосте — позиция известна из SeekHead. Читаем ровно
       тот кусок и планируем гашение уже в нём. */
    var extra = null;
    var seek = seekPositions(head, segBody, Math.min(segEnd, head.length));
    var rel = seek[ID.Tags];
    if (rel != null) {
        var abs = segBody + rel;
        if (abs >= head.length && abs < file.size) {
            var chunk = await slice(file, abs, Math.min(file.size, abs + MAX_REGION));
            extra = { at: abs, buf: chunk };
            planVoids(chunk, abs, 0, chunk.length, ops, plan);
        }
    }

    if (!plan.length) throw err('nothing-to-do');
    plan.sort(function (a, b) { return a.from - b.from; });

    /* Сборка: между диапазонами — ссылки на файл, сами диапазоны — Void. */
    var parts = [], pos = 0, wiped = [];
    for (var i = 0; i < plan.length; i++) {
        var r = plan[i];
        if (r.from < pos) continue;                       // перекрытие — пропускаем
        var v = voidBytes(r.to - r.from);
        if (!v) continue;                                 // короче двух байт — не гасится
        if (r.from > pos) parts.push(file.slice(pos, r.from));
        parts.push(v);
        wiped.push({ at: r.from, bytes: r.to - r.from });
        pos = r.to;
    }
    if (!wiped.length) throw err('nothing-to-do');
    if (pos < file.size) parts.push(file.slice(pos));

    return {
        blob: new Blob(parts, { type: file.type || 'video/x-matroska' }),
        wiped: wiped,
        bytesPatched: wiped.reduce(function (a, w) { return a + w.bytes; }, 0)
    };
}

window.EbmlProbe = { looksSupported: looksSupported, read: read, scrub: scrub, GPS_NAMES: GPS_NAMES };
/* Прежнее имя: модуль назывался ebml-tags.js, пока умел только теги. */
window.EbmlTags = window.EbmlProbe;
})();
